import { MandateState, Status } from '@smg-real-estate/seller-leads-schema';
import isEqual from 'lodash.isequal';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getLeadById, getLeads, INITIAL_PAGE } from '@/api/sellerLeads';
import { getQueryParams } from '@/helpers/SellerLeadsUtils/sellerLeadsUtils';
import {
  LeadSource,
  LeadSourceToOriginSource,
  QualifiedOptions,
  Sort,
  type DisplayableLead,
  type LeadsResponseBody,
  type SearchFilters,
  type SelectedStatus,
} from '@/models/sellerLead';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { adaptData, updateRefundProperties } from '@/views/SellerLeads/Details/adaptData';

export interface Filters {
  searchText: string;
  status: Status[];
  mandateState: MandateState[];
  source: LeadSource[];
  sortOrder: Sort;
  page: number;
  qualified: QualifiedOptions[];
}

export interface SellerLeadsState {
  filters: Filters;
  selectedLead: DisplayableLead | null;
  selectedStatus: SelectedStatus;
  hasStatusError: boolean;
  detailPgNotFound: boolean;
  detailPgLoading: boolean;
  agencyLeadsResponse: LeadsResponseBody | null;
  activeDistrictsSaved: boolean;
}

export const getInitialState = (): SellerLeadsState => ({
  filters: {
    searchText: '',
    status: Object.values(Status),
    source: Object.values(LeadSource),
    sortOrder: Sort.Desc,
    page: INITIAL_PAGE,
    qualified: Object.values(QualifiedOptions),
    mandateState: Object.values(MandateState),
  },
  selectedLead: null,
  selectedStatus: {
    status: Status.NewLeadReceived,
    mandateState: null,
  },
  hasStatusError: false,
  detailPgNotFound: false,
  detailPgLoading: false,
  agencyLeadsResponse: null,
  activeDistrictsSaved: false,
});

export const getDefaultFilters = (): Filters => {
  const queryParams = getQueryParams();

  return {
    searchText: queryParams.searchText,
    status: queryParams.status,
    source: queryParams.source,
    sortOrder: queryParams.sortOrder,
    page: queryParams.page,
    qualified: queryParams.qualified,
    mandateState: queryParams.mandateState,
  };
};

export const useSellerLeadsStore = defineStore(
  'sellerLeads',
  // eslint-disable-next-line max-statements -- setup store returns state, derived filters, and lead fetch together
  () => {
    const initial = getInitialState();
    const filters = ref<Filters>(getDefaultFilters());
    const selectedLead = ref<DisplayableLead | null>(initial.selectedLead);
    const selectedStatus = ref<SelectedStatus>(initial.selectedStatus);
    const hasStatusError = ref(initial.hasStatusError);
    const detailPgNotFound = ref(initial.detailPgNotFound);
    const detailPgLoading = ref(initial.detailPgLoading);
    const agencyLeadsResponse = ref<LeadsResponseBody | null>(initial.agencyLeadsResponse);
    const activeDistrictsSaved = ref(initial.activeDistrictsSaved);

    const hasFilters = computed(() => !isEqual(filters.value, getInitialState().filters));

    const searchFilters = computed<SearchFilters>(() => {
      const isSelectedAllStatus = Object.values(Status).length === filters.value.status.length;
      const isSelectedAllSource = Object.values(LeadSource).length === filters.value.source.length;
      const isSelectedAllQualification =
        Object.values(QualifiedOptions).length === filters.value.qualified.length;
      const isSelectedNoneQualification = filters.value.qualified.length === 0;
      const isSelectedNoneStatus = filters.value.status.length === 0;
      const isSelectedNoneSource = filters.value.source.length === 0;
      const isSelectedNoneMandateState = filters.value.mandateState.length === 0;
      const isSelectedAllMandateState =
        Object.values(MandateState).length === filters.value.mandateState.length;

      return {
        searchText: filters.value.searchText || undefined,
        status: isSelectedAllStatus || isSelectedNoneStatus ? undefined : filters.value.status,
        source:
          isSelectedAllSource || isSelectedNoneSource
            ? undefined
            : filters.value.source.map((item) => LeadSourceToOriginSource[item]).flat(),
        sortOrder: filters.value.sortOrder,
        qualified:
          isSelectedAllQualification || isSelectedNoneQualification
            ? undefined
            : filters.value.qualified,
        mandateState:
          isSelectedAllMandateState || isSelectedNoneMandateState
            ? undefined
            : filters.value.mandateState,
      };
    });

    const searchFiltersForUrl = computed(
      () => `${encodeURIComponent(JSON.stringify(searchFilters.value))}`,
    );

    const hasAgencyLeads = computed(() => (agencyLeadsResponse.value?.totalItems ?? 0) > 0);

    const setFilters = (nextFilters: Filters): void => {
      filters.value = nextFilters;
    };

    const setSelectedLead = (lead: DisplayableLead | null): void => {
      selectedLead.value = lead ? updateRefundProperties(lead) : null;
      selectedStatus.value = {
        status: lead?.status ?? Status.NewLeadReceived,
        mandateState: lead?.mandateState ?? null,
      };
      detailPgLoading.value = false;
    };

    const setSelectedStatus = (nextStatus: SelectedStatus): void => {
      selectedStatus.value = nextStatus;
    };

    const setHasStatusError = (nextHasStatusError: boolean): void => {
      hasStatusError.value = nextHasStatusError;
    };

    const setDetailPageError = (notFound: boolean): void => {
      detailPgNotFound.value = notFound;
      detailPgLoading.value = false;
    };

    const getLeadDetailsById = async (leadId: string): Promise<void> => {
      const response = await getLeadById(leadId);
      if (!response || !('statusCode' in response)) {
        setDetailPageError(true);
        return;
      }

      const { statusCode, results } = response;
      if (statusCode == 200 && !!results) {
        setSelectedLead(adaptData(results));
      } else if (statusCode == 404 || !results) {
        setDetailPageError(true);
      } else {
        setDetailPageError(false);
      }
    };

    const checkSelectedLead = (leadId: string): void => {
      detailPgNotFound.value = false;
      if (!selectedLead.value || selectedLead.value.leadId != leadId) {
        detailPgLoading.value = true;
        void getLeadDetailsById(leadId);
      }
    };

    const setAgencyLeadsResponse = (response: LeadsResponseBody | null): void => {
      agencyLeadsResponse.value = response;
    };

    const setActiveDistrictsSaved = (saved: boolean): void => {
      activeDistrictsSaved.value = saved;
    };

    const fetchAgencyLeads = async (): Promise<void> => {
      const activeAgencyId = useAgencyStore().activeAgencyId;
      if (!activeAgencyId) {
        setAgencyLeadsResponse(null);
        return;
      }

      const response = await getLeads(INITIAL_PAGE, { sortOrder: Sort.Desc });
      setAgencyLeadsResponse(response);
    };

    return {
      filters,
      selectedLead,
      selectedStatus,
      hasStatusError,
      detailPgNotFound,
      detailPgLoading,
      agencyLeadsResponse,
      activeDistrictsSaved,
      hasFilters,
      searchFilters,
      searchFiltersForUrl,
      hasAgencyLeads,
      setFilters,
      setSelectedLead,
      setSelectedStatus,
      setHasStatusError,
      checkSelectedLead,
      setDetailPageError,
      setAgencyLeadsResponse,
      setActiveDistrictsSaved,
      fetchAgencyLeads,
    };
  },
);
